import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Clock, 
  Plus, 
  Trash2, 
  Calendar,
  AlertTriangle,
  Loader2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

// Génère les options d'heure (00:00 à 23:45 par tranche de 15 min)
const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h.toString().padStart(2, '0');
      const min = m.toString().padStart(2, '0');
      options.push(`${hour}:${min}`);
    }
  }
  options.push('23:59');
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

export default function HoursManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingHours, setOpeningHours] = useState({});
  const [exceptionalClosures, setExceptionalClosures] = useState([]);
  const [specialHours, setSpecialHours] = useState([]);
  const [prepTime, setPrepTime] = useState(15);
  const [slotInterval, setSlotInterval] = useState(15);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  // Order acceptance state
  const [orderHours, setOrderHours] = useState({});
  const [ordersPaused, setOrdersPaused] = useState(false);
  const [ordersPausedMessage, setOrdersPausedMessage] = useState('Trop de commandes en cours, revenez ultérieurement');
  const [savingOrders, setSavingOrders] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);

  // Dialog states
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [specialDialogOpen, setSpecialDialogOpen] = useState(false);
  const [newClosure, setNewClosure] = useState({ date: '', reason: '', all_day: true });
  const [newSpecial, setNewSpecial] = useState({ date: '', reason: '', slots: [{ open: '11:30', close: '23:00' }] });

  useAuth();

  const fetchHours = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hours/restaurant-dashboard/hours`);
      
      setOpeningHours(response.data.opening_hours || {});
      setExceptionalClosures(response.data.exceptional_closures || []);
      setSpecialHours(response.data.special_hours || []);
      setPrepTime(response.data.prep_time_minutes || 15);
      setSlotInterval(response.data.slot_interval_minutes || 15);
    } catch (error) {
      toast.error('Erreur lors du chargement des horaires');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHours();
    fetchOrderSettings();
  }, [fetchHours]);

  const fetchOrderSettings = async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/order-settings`);
      setOrderHours(response.data.order_hours || {});
      setOrdersPaused(response.data.orders_paused || false);
      setOrdersPausedMessage(response.data.orders_paused_message || 'Trop de commandes en cours, revenez ultérieurement');
    } catch (error) {
      console.error('Error fetching order settings');
    }
  };

  const handleTogglePause = async () => {
    setTogglingPause(true);
    try {
      const response = await axios.post(`${API}/restaurant-dashboard/toggle-orders-pause`, {});
      setOrdersPaused(response.data.orders_paused);
      toast.success(response.data.message);
    } catch {
      toast.error('Erreur');
    } finally {
      setTogglingPause(false);
    }
  };

  const saveOrderSettings = async () => {
    setSavingOrders(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/order-settings`, {
        order_hours: orderHours,
        orders_paused_message: ordersPausedMessage
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success('Plages de commande enregistrées');
      setHasOrderChanges(false);
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingOrders(false);
    }
  };

  // Order hours helpers
  const ensureOrderDay = (day) => {
    return orderHours[day] || { enabled: false, slots: [] };
  };

  const toggleOrderDayEnabled = (day) => {
    const dayData = ensureOrderDay(day);
    setOrderHours(prev => ({
      ...prev,
      [day]: { ...dayData, enabled: !dayData.enabled, slots: dayData.slots.length ? dayData.slots : [{ open: '09:00', close: '12:00' }] }
    }));
    setHasOrderChanges(true);
  };

  const addOrderSlot = (day) => {
    const dayData = ensureOrderDay(day);
    setOrderHours(prev => ({
      ...prev,
      [day]: { ...dayData, slots: [...dayData.slots, { open: '14:00', close: '18:00' }] }
    }));
    setHasOrderChanges(true);
  };

  const removeOrderSlot = (day, slotIndex) => {
    const dayData = ensureOrderDay(day);
    const newSlots = dayData.slots.filter((_, i) => i !== slotIndex);
    setOrderHours(prev => ({
      ...prev,
      [day]: { ...dayData, slots: newSlots }
    }));
    setHasOrderChanges(true);
  };

  const updateOrderSlot = (day, slotIndex, field, value) => {
    const dayData = ensureOrderDay(day);
    const newSlots = [...dayData.slots];
    newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
    setOrderHours(prev => ({
      ...prev,
      [day]: { ...dayData, slots: newSlots }
    }));
    setHasOrderChanges(true);
  };

  // Initialise un jour s'il n'existe pas
  const ensureDay = (day) => {
    if (!openingHours[day]) {
      return { is_open: false, slots: [] };
    }
    return openingHours[day];
  };

  // Toggle jour ouvert/fermé
  const toggleDayOpen = (day) => {
    const dayData = ensureDay(day);
    setOpeningHours(prev => ({
      ...prev,
      [day]: {
        ...dayData,
        is_open: !dayData.is_open,
        slots: dayData.is_open ? [] : [{ open: '11:30', close: '14:30' }, { open: '18:00', close: '23:00' }]
      }
    }));
    setHasChanges(true);
  };

  // Ajouter une plage horaire
  const addSlot = (day) => {
    const dayData = ensureDay(day);
    const lastSlot = dayData.slots[dayData.slots.length - 1];
    const newSlot = lastSlot 
      ? { open: lastSlot.close, close: '23:00' }
      : { open: '11:30', close: '14:30' };
    
    setOpeningHours(prev => ({
      ...prev,
      [day]: {
        ...dayData,
        slots: [...dayData.slots, newSlot]
      }
    }));
    setHasChanges(true);
  };

  // Supprimer une plage horaire
  const removeSlot = (day, index) => {
    const dayData = ensureDay(day);
    setOpeningHours(prev => ({
      ...prev,
      [day]: {
        ...dayData,
        slots: dayData.slots.filter((_, i) => i !== index)
      }
    }));
    setHasChanges(true);
  };

  // Modifier une plage horaire
  const updateSlot = (day, index, field, value) => {
    const dayData = ensureDay(day);
    const newSlots = [...dayData.slots];
    newSlots[index] = { ...newSlots[index], [field]: value };
    
    setOpeningHours(prev => ({
      ...prev,
      [day]: {
        ...dayData,
        slots: newSlots
      }
    }));
    setHasChanges(true);
  };

  // Sauvegarder les horaires
  const saveHours = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/hours/restaurant-dashboard/hours`, {
        opening_hours: openingHours
      });
      
      // Sauvegarder les paramètres
      await axios.put(
        `${API}/hours/restaurant-dashboard/hours/settings?prep_time_minutes=${prepTime}&slot_interval_minutes=${slotInterval}`,
        {}
      );
      
      toast.success('Horaires enregistrés');
      setHasChanges(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  // Ajouter une fermeture exceptionnelle
  const addClosure = async () => {
    try {
      await axios.post(`${API}/hours/restaurant-dashboard/hours/closure`, newClosure);
      toast.success('Fermeture ajoutée');
      setClosureDialogOpen(false);
      setNewClosure({ date: '', reason: '', all_day: true });
      fetchHours();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  // Supprimer une fermeture
  const deleteClosure = async (id) => {
    try {
      await axios.delete(`${API}/hours/restaurant-dashboard/hours/closure/${id}`);
      toast.success('Fermeture supprimée');
      fetchHours();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Ajouter des horaires spéciaux
  const addSpecialHours = async () => {
    try {
      await axios.post(`${API}/hours/restaurant-dashboard/hours/special`, newSpecial);
      toast.success('Horaires spéciaux ajoutés');
      setSpecialDialogOpen(false);
      setNewSpecial({ date: '', reason: '', slots: [{ open: '11:30', close: '23:00' }] });
      fetchHours();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  // Supprimer des horaires spéciaux
  const deleteSpecialHours = async (id) => {
    try {
      await axios.delete(`${API}/hours/restaurant-dashboard/hours/special/${id}`);
      toast.success('Horaires spéciaux supprimés');
      fetchHours();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Horaires d'ouverture</h2>
          <p className="text-gray-500 mt-1">Gérez vos horaires, fermetures et créneaux de retrait</p>
        </div>
        {hasChanges && (
          <Button onClick={saveHours} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        )}
      </div>

      {/* Horaires hebdomadaires */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            Horaires réguliers
          </CardTitle>
          <CardDescription>
            Définissez vos horaires d'ouverture pour chaque jour de la semaine
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {DAYS.map(({ key, label }) => {
            const dayData = ensureDay(key);
            const isExpanded = expandedDay === key;
            
            return (
              <div 
                key={key} 
                className={`border rounded-xl p-4 transition-all ${
                  dayData.is_open ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                {/* Header du jour */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={dayData.is_open}
                      onCheckedChange={() => toggleDayOpen(key)}
                    />
                    <span className={`font-medium ${dayData.is_open ? 'text-gray-900' : 'text-gray-400'}`}>
                      {label}
                    </span>
                    {!dayData.is_open && (
                      <Badge variant="secondary" className="text-gray-500">Fermé</Badge>
                    )}
                  </div>
                  
                  {dayData.is_open && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedDay(isExpanded ? null : key)}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {dayData.slots.length} plage(s)
                    </Button>
                  )}
                </div>

                {/* Plages horaires (expandable) */}
                {dayData.is_open && isExpanded && (
                  <div className="mt-4 space-y-3 pl-12">
                    {dayData.slots.map((slot, index) => (
                      <div key={`${key}-slot-${index}-${slot.open}-${slot.close}`} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-100">
                        <Select
                          value={slot.open}
                          onValueChange={(value) => updateSlot(key, index, 'open', value)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <span className="text-gray-400">à</span>
                        
                        <Select
                          value={slot.close}
                          onValueChange={(value) => updateSlot(key, index, 'close', value)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TIME_OPTIONS.map(time => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Input
                          placeholder="Label (optionnel)"
                          value={slot.label || ''}
                          onChange={(e) => updateSlot(key, index, 'label', e.target.value)}
                          className="flex-1 max-w-40"
                        />
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSlot(key, index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addSlot(key)}
                      className="w-full border-dashed"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter une plage
                    </Button>
                  </div>
                )}
                
                {/* Aperçu rapide des plages */}
                {dayData.is_open && !isExpanded && dayData.slots.length > 0 && (
                  <div className="mt-2 pl-12 flex flex-wrap gap-2">
                    {dayData.slots.map((slot, index) => (
                      <Badge key={`${key}-badge-${slot.open}-${slot.close}`} variant="outline" className="text-emerald-700 border-emerald-200">
                        {slot.open} - {slot.close}
                        {slot.label && <span className="ml-1 text-gray-400">({slot.label})</span>}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Fermetures exceptionnelles */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-red-500" />
                Fermetures exceptionnelles
              </CardTitle>
              <CardDescription>
                Jours fériés, congés, événements...
              </CardDescription>
            </div>
            <Dialog open={closureDialogOpen} onOpenChange={setClosureDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouvelle fermeture exceptionnelle</DialogTitle>
                  <DialogDescription>
                    Ajoutez une fermeture ponctuelle (jour férié, congés...)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={newClosure.date}
                      onChange={(e) => setNewClosure(prev => ({ ...prev, date: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label>Motif</Label>
                    <Input
                      placeholder="Ex: Jour de l'An, Congés annuels..."
                      value={newClosure.reason}
                      onChange={(e) => setNewClosure(prev => ({ ...prev, reason: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newClosure.all_day}
                      onCheckedChange={(checked) => setNewClosure(prev => ({ ...prev, all_day: checked }))}
                    />
                    <Label>Fermé toute la journée</Label>
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Annuler</Button>
                  </DialogClose>
                  <Button onClick={addClosure} disabled={!newClosure.date || !newClosure.reason}>
                    Ajouter
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {exceptionalClosures.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucune fermeture programmée</p>
          ) : (
            <div className="space-y-2">
              {exceptionalClosures.map((closure) => (
                <div 
                  key={closure.id} 
                  className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="font-medium text-gray-900">{closure.reason}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(closure.date).toLocaleDateString('fr-FR', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        {closure.all_day ? ' - Toute la journée' : ''}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteClosure(closure.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Horaires spéciaux */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                Horaires spéciaux
              </CardTitle>
              <CardDescription>
                Horaires modifiés pour des occasions particulières
              </CardDescription>
            </div>
            <Dialog open={specialDialogOpen} onOpenChange={setSpecialDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Horaires spéciaux</DialogTitle>
                  <DialogDescription>
                    Définissez des horaires différents pour une date spécifique
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={newSpecial.date}
                      onChange={(e) => setNewSpecial(prev => ({ ...prev, date: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label>Motif</Label>
                    <Input
                      placeholder="Ex: Réveillon de Noël..."
                      value={newSpecial.reason}
                      onChange={(e) => setNewSpecial(prev => ({ ...prev, reason: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Horaires</Label>
                    {newSpecial.slots.map((slot, index) => (
                      <div key={`special-slot-${index}-${slot.open}-${slot.close}`} className="flex items-center gap-2 mt-2">
                        <Input
                          type="time"
                          value={slot.open}
                          onChange={(e) => {
                            const newSlots = [...newSpecial.slots];
                            newSlots[index].open = e.target.value;
                            setNewSpecial(prev => ({ ...prev, slots: newSlots }));
                          }}
                        />
                        <span>à</span>
                        <Input
                          type="time"
                          value={slot.close}
                          onChange={(e) => {
                            const newSlots = [...newSpecial.slots];
                            newSlots[index].close = e.target.value;
                            setNewSpecial(prev => ({ ...prev, slots: newSlots }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Annuler</Button>
                  </DialogClose>
                  <Button onClick={addSpecialHours} disabled={!newSpecial.date || !newSpecial.reason}>
                    Ajouter
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {specialHours.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucun horaire spécial programmé</p>
          ) : (
            <div className="space-y-2">
              {specialHours.map((special) => (
                <div 
                  key={special.id} 
                  className="flex items-center justify-between p-3 bg-orange-50 border border-orange-100 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="font-medium text-gray-900">{special.reason}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(special.date).toLocaleDateString('fr-FR', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        {' - '}
                        {special.slots.map(s => `${s.open}-${s.close}`).join(', ')}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteSpecialHours(special.id)}
                    className="text-orange-500 hover:text-orange-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paramètres */}
      <Card>
        <CardHeader>
          <CardTitle>Paramètres de commande</CardTitle>
          <CardDescription>
            Configuration des créneaux de retrait
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Temps de préparation (minutes)</Label>
              <Select
                value={prepTime.toString()}
                onValueChange={(value) => {
                  setPrepTime(parseInt(value));
                  setHasChanges(true);
                }}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 15, 20, 30, 45, 60].map(min => (
                    <SelectItem key={min} value={min.toString()}>{min} minutes</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">
                Délai minimum entre la commande et le retrait
              </p>
            </div>
            <div>
              <Label>Intervalle entre créneaux (minutes)</Label>
              <Select
                value={slotInterval.toString()}
                onValueChange={(value) => {
                  setSlotInterval(parseInt(value));
                  setHasChanges(true);
                }}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 15, 20, 30].map(min => (
                    <SelectItem key={min} value={min.toString()}>{min} minutes</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">
                Espacement entre chaque créneau de retrait proposé
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ==================== ORDER ACCEPTANCE SECTION ==================== */}
      <Card className="border-amber-200" data-testid="order-acceptance-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-600" />
            Prise de commandes
          </CardTitle>
          <CardDescription>
            Configurez quand vous acceptez les commandes et bloquez-les manuellement si besoin
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Manual Pause Toggle */}
          <div className={`flex items-center justify-between gap-4 p-4 rounded-xl border ${ordersPaused ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="orders-pause-toggle">
            <div>
              <p className={`font-medium ${ordersPaused ? 'text-red-700' : 'text-emerald-700'}`}>
                {ordersPaused ? 'Commandes en pause' : 'Commandes acceptées'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {ordersPaused ? 'Aucune nouvelle commande ne sera acceptée' : 'Les clients peuvent passer commande'}
              </p>
            </div>
            <Switch
              checked={!ordersPaused}
              onCheckedChange={handleTogglePause}
              disabled={togglingPause}
              data-testid="toggle-orders-pause-hours"
            />
          </div>

          {/* Pause Message */}
          <div>
            <Label className="text-sm font-medium">Message affiché aux clients (quand en pause)</Label>
            <Input
              value={ordersPausedMessage}
              onChange={(e) => {
                setOrdersPausedMessage(e.target.value);
                setHasOrderChanges(true);
              }}
              maxLength={200}
              className="mt-1.5"
              placeholder="Trop de commandes en cours, revenez ultérieurement"
              data-testid="orders-paused-message"
            />
            <p className="text-xs text-gray-400 mt-1">{ordersPausedMessage.length}/200 caractères</p>
          </div>

          {/* Order Hours */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">Plages horaires d'acceptation des commandes</Label>
            <p className="text-xs text-gray-500 mb-4">
              Si aucune plage n'est configurée, les commandes sont acceptées à tout moment (tant que le commerce est ouvert).
            </p>
            <div className="space-y-3">
              {DAYS.map(({ key, label }) => {
                const dayData = ensureOrderDay(key);
                return (
                  <div key={key} className={`p-3 rounded-xl border ${dayData.enabled ? 'border-amber-200 bg-amber-50/50' : 'border-gray-200'}`} data-testid={`order-day-${key}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={dayData.enabled}
                          onCheckedChange={() => toggleOrderDayEnabled(key)}
                          data-testid={`order-toggle-${key}`}
                        />
                        <span className={`font-medium text-sm ${dayData.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                          {label}
                        </span>
                      </div>
                      {dayData.enabled && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addOrderSlot(key)}
                          className="text-amber-600 hover:text-amber-700"
                          data-testid={`order-add-slot-${key}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Créneau
                        </Button>
                      )}
                    </div>
                    
                    {dayData.enabled && dayData.slots.length > 0 && (
                      <div className="mt-3 space-y-2 pl-12">
                        {dayData.slots.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Select
                              value={slot.open}
                              onValueChange={(v) => updateOrderSlot(key, idx, 'open', v)}
                            >
                              <SelectTrigger className="w-28 h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TIME_OPTIONS.map(t => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="text-gray-400 text-sm">à</span>
                            <Select
                              value={slot.close}
                              onValueChange={(v) => updateOrderSlot(key, idx, 'close', v)}
                            >
                              <SelectTrigger className="w-28 h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TIME_OPTIONS.map(t => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeOrderSlot(key, idx)}
                              className="text-red-400 hover:text-red-600 h-9 w-9 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {dayData.enabled && dayData.slots.length === 0 && (
                      <p className="text-xs text-gray-400 mt-2 pl-12">Aucun créneau — commandes acceptées toute la journée</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Save button */}
          {hasOrderChanges && (
            <Button
              onClick={saveOrderSettings}
              disabled={savingOrders}
              className="w-full bg-amber-500 hover:bg-amber-600"
              data-testid="save-order-settings"
            >
              {savingOrders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer les plages de commande
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
